<script lang="ts">
	import DOMPurify from 'dompurify';
	import { onMount, createEventDispatcher } from 'svelte';
	import type { EditorAttrs } from '$lib/components/editor/editor.type';
	import { editorStore } from '$lib/components/editor/editor.store';
	import { postStore } from '$lib/posts';
	import {
		handleImageUpload,
		getResponsiveImageUrls,
		getFullResolutionImageUrl,
		getPublicImageUrl
	} from './imageHandler';
	import type Quill from 'quill';
	// import ImageCompress from 'quill-image-compress';
	import { browser } from '$app/environment';
	import { get } from 'svelte/store';
	import { profileStore } from '$lib/user';

	let loading = true;
	let quill: Quill;
	let editor: HTMLElement;
	let myValue = 1600;
	const dispatch = createEventDispatcher();

	export let delta: string;
	export let content: EditorAttrs = { html: '', text: '', delta: '' };

	export let toolbarOptions = [
		[{ header: 1 }, { header: 2 }, 'blockquote', 'link', 'image', 'video'],
		['bold', 'italic', 'underline', 'strike'],
		[{ color: [] }, { background: [] }],
		[{ list: 'ordered' }, { list: 'bullet' }],
		[{ align: [] }],
		['clean']
	];
	// , { list: 'bullet' }

	export let view = false;
	export let toolbar = !view; // display the toolbar by default if we aren't in view mode

	let config: any = {};
	// TODO: this will be removed, as handleImageUpload does this now
	// const imageCompress = {
	// 	quality: 0.8, // .7 is default
	// 	maxWidth: 1200, // 1000 isdefault
	// 	maxHeight: 800, // default
	// 	imageType: 'image/jpeg', // default
	// 	debug: true, // default
	// 	suppressErrorLogging: false, // default
	// 	insertIntoEditor: undefined // default
	// };

	if (toolbar) {
		config = {
			modules: {
				toolbar: {
					container: toolbarOptions,
					handlers: {
						image: async function () {
							const input = document.createElement('input');
							input.setAttribute('type', 'file');
							input.setAttribute('accept', 'image/*');
							input.click();

							input.onchange = async () => {
								if (!input.files?.length) return;
								const file = input.files[0];
								const range = quill.getSelection(true);
								// Insert text placeholder at cursor
								const placeholderText = 'Uploading image...';
								quill.insertText(range.index, placeholderText, { color: '#a259ff' });
								const placeholderIndex = range.index;
								const placeholderLength = placeholderText.length;
								// Start upload
								const relativePath = await handleImageUpload(file);
								quill.deleteText(placeholderIndex, placeholderLength);
								if (relativePath) {
									const publicUrl = getPublicImageUrl(relativePath);
									quill.insertEmbed(placeholderIndex, 'image', publicUrl);
								}
							};
						}
					}
				}
			},
			theme: 'snow',
			placeholder: 'What would you like to say?..'
		};
	} else {
		config = {
			modules: { toolbar: false },
			theme: 'snow',
			placeholder: 'What would you like to say?..',
			readOnly: true
		};
	}

	let containerClass = 'border border-[#639]';
	$: containerClass = !config.modules.toolbar ? '' : 'border border-[#639] rounded-b-md';

	// Helper function to detect if an image source is base64
	const isBase64Image = (src: string): boolean => {
		return src.startsWith('data:image/');
	};

	// Helper to check if user has required role
	const hasImageAccessRole = (): boolean => {
		const profile = get(profileStore);
		const roles = ['grants', 'media', 'admin'];
		return roles.includes(profile?.system_role || '');
	};

	// Function to process content before setting it in the editor
	const processContent = (content: string) => {
		if (!content) return content;

		try {
			const ops = JSON.parse(content)['ops'];
			// Process each operation to ensure images are handled correctly
			const processedOps = ops.map((op: any) => {
				if (op.insert?.image) {
					const imgSrc = op.insert.image;
					// If it's a base64 image, keep it as is
					if (isBase64Image(imgSrc)) {
						return op;
					}

					// If it's a URL and we're in view mode, use responsive images
					if (view && !imgSrc.startsWith('data:')) {
						// Instead of creating HTML, just use the optimized URL
						const responsiveUrls = getResponsiveImageUrls(imgSrc);
						// Use the 800px width version as default
						op.insert.image = responsiveUrls[1].url;
					} else if (!imgSrc.startsWith('http')) {
						// If it's a relative URL, make it absolute
						op.insert.image = imgSrc.startsWith('/') ? imgSrc : `/${imgSrc}`;
					}
				}
				return op;
			});
			return JSON.stringify({ ops: processedOps });
		} catch (e) {
			console.error('Error processing content:', e);
			return content;
		}
	};

	// Modify setContent to handle both types of images
	const setContent = () => {
		// Get the raw HTML
		const rawHtml = quill.root.innerHTML;

		// Process any images in the HTML to ensure they're properly handled
		const processedHtml = rawHtml.replace(/<img[^>]+src="([^"]+)"[^>]*>/g, (match, src) => {
			// If it's a base64 image, keep it as is
			if (isBase64Image(src)) {
				return match;
			}
			// If it's a URL, ensure it's properly formatted
			if (!src.startsWith('http')) {
				return match.replace(src, src.startsWith('/') ? src : `/${src}`);
			}
			return match;
		});

		content = {
			html: DOMPurify.sanitize(processedHtml),
			text: quill.getText(0),
			delta: JSON.stringify(quill.getContents())
		};
		$editorStore = content;
		$postStore.contents = content.delta;
	};

	let viewStyle: string;

	$: viewStyle = view ? 'min-h-fit' : '';

	onMount(() => {
		if (browser) {
			// Add global click handler for images
			window.handleImageClick = (event: MouseEvent, imageSrc: string) => {
				const fullResUrl = getFullResolutionImageUrl(imageSrc);
				if (fullResUrl) {
					// Open in new tab
					window.open(fullResUrl, '_blank');
				}
			};

			// Define a function to handle the import and initialization
			const initializeQuill = async () => {
				const { default: Quill } = await import('quill');
				const { default: ImageCompress } = await import('quill-image-compress');
				Quill.register('modules/imageCompress', ImageCompress);
				quill = new Quill(editor, config);

				if (delta) {
					// Process the content before setting it
					const processedDelta = processContent(delta);
					let ops = JSON.parse(processedDelta)['ops'];
					quill.setContents(ops);
				}
				quill.on('text-change', function () {
					setContent();
				});
				quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
					delta.ops.forEach((op) => {
						if (op.attributes && op.attributes.color) {
							delete op.attributes.color; // Remove any inline color styles
						}
					});
					return delta;
				});

				quill.root.addEventListener('paste', () => {
					setTimeout(() => {
						// Ensure all text is white and links are blue
						const elements = quill.root.querySelectorAll('*');
						Array.from(elements).forEach((el: Element) => {
							if (el instanceof HTMLElement) {
								if (!el.style.color || el.style.color === 'black') {
									el.style.color = '#F3F4F6'; // Force text color to white
								}
								if (el.tagName === 'A') {
									el.style.color = '#1E90FF'; // Force link color to blue
								}
							}
						});

						const paragraphs = quill.root.querySelectorAll('p');
						Array.from(paragraphs).forEach((p: Element) => {
							if (p instanceof HTMLParagraphElement) {
								p.style.marginBottom = '1em'; // Force spacing between paragraphs
							}
						});

						setContent();
					}, 0);
				});

				setContent();
				dispatch('load');
			};

			initializeQuill();
			loading = false;
		}
	});

	// Add this after the onMount function
	$: if (view) {
		// Add click handlers to images after they're rendered
		setTimeout(() => {
			const images = editor?.querySelectorAll('.ql-editor img');
			if (images) {
				images.forEach((img) => {
					if (hasImageAccessRole()) {
						img.classList.add('authorized');
						img.addEventListener('click', () => {
							const imageElement = img as HTMLImageElement;
							const fullResUrl = getFullResolutionImageUrl(imageElement.src);
							if (fullResUrl) {
								window.open(fullResUrl, '_blank');
							}
						});
					}
				});
			}
		}, 0);
	}
</script>

<svelte:window bind:innerWidth={myValue} />

{#if loading}
	<div class="h-96">
		<!-- Placeholder content (you can style this) -->
	</div>
{:else}
	<div
		class="h-full max-w-full md:max-w-[600px] lg:max-w-[650px] xl:max-w-[800px] xl:px-3 2xl:max-w-full 3xl:w-[1000px] mx-auto pt-5 {viewStyle}"
	>
		<div class={containerClass} bind:this={editor} />
	</div>
{/if}

<style>
	.ql-editor {
		color: #f3f4f6;
	}

	.ql-editor-image {
		max-width: 100%;
		height: auto;
		display: block;
		margin: 1em auto;
	}

	/* Add styles for the picture element */
	.ql-editor picture {
		display: block;
		margin: 1em auto;
		max-width: 100%;
	}

	/* .ql-editor a {
		color: #1e90ff; 
	}

	.ql-editor p {
		margin-bottom: 1em;
	} */

	.ql-editor-image-container {
		display: block;
		margin: 1em auto;
		max-width: 100%;
	}

	/* Only show hover effects and cursor for authorized users */
	.ql-editor-image-container.authorized {
		cursor: pointer;
		transition: opacity 0.2s ease;
	}

	.ql-editor-image-container.authorized:hover {
		opacity: 0.9;
	}

	/* Only show magnifying glass for authorized users */
	.ql-editor-image-container.authorized::after {
		content: '🔍';
		position: absolute;
		top: 10px;
		right: 10px;
		background: rgba(0, 0, 0, 0.5);
		color: white;
		padding: 4px 8px;
		border-radius: 4px;
		font-size: 14px;
		opacity: 0;
		transition: opacity 0.2s ease;
	}

	.ql-editor-image-container.authorized:hover::after {
		opacity: 1;
	}

	.ql-editor img {
		max-width: 100%;
		height: auto;
		display: block;
		margin: 1em auto;
	}

	.ql-editor img.authorized {
		cursor: pointer;
		transition: opacity 0.2s ease;
	}

	.ql-editor img.authorized:hover {
		opacity: 0.9;
	}

	.ql-editor img.authorized::after {
		content: '🔍';
		position: absolute;
		top: 10px;
		right: 10px;
		background: rgba(0, 0, 0, 0.5);
		color: white;
		padding: 4px 8px;
		border-radius: 4px;
		font-size: 14px;
		opacity: 0;
		transition: opacity 0.2s ease;
	}

	.ql-editor img.authorized:hover::after {
		opacity: 1;
	}
</style>
